import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/Loading";
import axiosInstance from "../../AxiosInstance";
import { URLS } from "../../API/API endpoints";
import OTPModal from "../../modals/verifyOTP";
import { signupUser } from "../../components/Actions/signupActions";
import { signupActions } from "../../components/Redux Slices/signupSlice";
import SubStoreLocation from "../../app/subStore/SubstoreRegister/SubstoreLocation";
import SubStoreBasicDeatils from "../../app/subStore/SubstoreRegister/SubStoreBasicDetails";
const CreateWarehouse = ({ isEdit, editData, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const master_store_id = useSelector(
    (state) => state?.login?.user?.result?.company_id
  );

  const [loading, setLoading] = useState(false);
  const [shopLocationData, setShopLocationData] = useState(null);
  const [basicDetailsData, setBasicDetailsData] = useState(null);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [oldPhoneNumber, setOldPhoneNumber] = useState(null);
  const [oldEmail, setOldEmail] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const shopLocationRef = useRef(null);
  const basicDetailsRef = useRef(null);
  const basicDataSend = basicDetailsRef.current?.getData?.();

  // ------------------------
  // PREFILL DATA WHEN EDIT
  // ------------------------
  useEffect(() => {
    if (isEdit && editData) {
      const locData = {
        street: editData?.street || "",
        street2: editData?.street2 || "",
        zip: editData?.zip || "",
        state: editData?.state || "",
        latitude: editData?.latitude || "",
        longitude: editData?.longitude || "",
      };

      const basicData = {
        email: editData?.email || "",
        phone: editData?.phone || "",
        warehouse_name: editData?.warehouse_name || "",
        password: editData?.password || "",
      };

      setShopLocationData(locData);
      setBasicDetailsData(basicData);
      setOldPhoneNumber(editData?.phone || "");
      setOldEmail(editData?.email || "");
      setIsDirty(false);
    }
  }, [isEdit, editData]);

  // ------------------------
  // VALIDATION CHECK
  // ------------------------
  useEffect(() => {
    const checkFormValidity = () => {
      const shopData = shopLocationRef.current?.getData?.();
      const basicData = basicDetailsRef.current?.getData?.();
      if (
        shopData &&
        basicData &&
        basicData.phone &&
        basicData.warehouse_name &&
        basicData.email &&
        shopData.street &&
        shopData.zip
      ) {
        setIsFormValid(true);
      } else {
        setIsFormValid(false);
      }
    };

    const interval = setInterval(checkFormValidity, 300);
    return () => clearInterval(interval);
  }, []);

  // ------------------------
  // DIRTY STATE DETECTION
  // ------------------------
  useEffect(() => {
    if (!isEdit || !editData) return;

    const currentLoc = shopLocationRef.current?.getData?.();
    const currentBasic = basicDetailsRef.current?.getData?.();

    const originalLoc = {
      street: editData?.street || "",
      street2: editData?.street2 || "",
      zip: editData?.zip || "",
      state: editData?.state || "",
      latitude: editData?.latitude || "",
      longitude: editData?.longitude || "",
    };

    const originalBasic = {
      email: editData?.email || "",
      phone: editData?.phone || "",
      warehouse_name: editData?.warehouse_name || "",
      password: editData?.password || "",
    };

    const changed =
      JSON.stringify(currentLoc) !== JSON.stringify(originalLoc) ||
      JSON.stringify(currentBasic) !== JSON.stringify(originalBasic);

    setIsDirty(changed);
  }, [shopLocationData, basicDetailsData, isEdit, editData]);

  // ------------------------
  // CANCEL BUTTON HANDLER
  // ------------------------
  const handleCancel = () => {
    if (isEdit && editData) {
      const locData = {
        street: editData?.street || "",
        street2: editData?.street2 || "",
        zip: editData?.zip || "",
        state: editData?.state || "",
        latitude: editData?.latitude || "",
        longitude: editData?.longitude || "",
      };

      const basicData = {
        email: editData?.email || "",
        phone: editData?.phone || "",
        warehouse_name: editData?.warehouse_name || "",
        password: editData?.password || "",
      };

      // Restore to old state
      setShopLocationData(locData);
      setBasicDetailsData(basicData);
      shopLocationRef.current?.setData?.(locData);
      basicDetailsRef.current?.setData?.(basicData);
      setIsDirty(false);
      onClose();
    } else {
      // New mode: just clear
      shopLocationRef.current?.resetForm?.();
      basicDetailsRef.current?.resetForm?.();
      setShopLocationData(null);
      setBasicDetailsData(null);
      setIsFormValid(false);
    }
  };

  // ------------------------
  // VERIFY SUBSTORE DETAILS
  // ------------------------
  const verifySubStoreDetails = async (basicData) => {
    const payload = {
      email: basicData?.email.toLowerCase(),
      phone: basicData?.phone?.startsWith("0")
        ? basicData?.phone.substring(1)
        : basicData?.phone,
      warehouse_name: basicData?.warehouse_name,
    };

    try {
      setLoading(true);
      const res = await axiosInstance.post(URLS.VERIFY_WAREHOUSE_DATA, payload);

      const result = res?.data?.result;

      // If API returns a known failure status
      if (result?.status_code !== 200) {
        toast.error(result?.status || "Verification failed");
        return false;
      }

      // In case backend returns specific errors (e.g., phone/email already exist)
      if (
        result?.status === "Email already exists" ||
        result?.status === "Phone number already exists" ||
        result?.status === "Warehouse name already exists"
      ) {
        toast.error(t(result?.status));
        return false;
      }

      return true; // Verification passed
    } catch (error) {
      toast.error("Verification failed. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ------------------------
  // SAVE / UPDATE LOGIC
  // ------------------------

  const handleSave = async () => {
    const shopData = shopLocationRef.current?.getData?.();
    const basicData = basicDetailsRef.current?.getData?.();

    if (!shopData || !basicData) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    // ✅ Step 1: Verify substore details first
    if (!isEdit) {
      const isVerified = await verifySubStoreDetails(basicData);
      if (!isVerified) return; // stop if verification fails
    }

    // ✅ Step 2: Continue normal flow
    if (isEdit) {
      if (basicData?.phone !== oldPhoneNumber) {
        setPhoneNumber(basicData);
        sendOtp(basicData?.phone);
      } else {
        handleUpdate(basicData, shopData);
      }
    } else {
      setPhoneNumber(basicData);
      sendOtp(basicData?.phone);
    }
  };

  const handleOtpVerified = async () => {
    const shopData = shopLocationRef.current?.getData?.();
    const basicData = basicDetailsRef.current?.getData?.();

    if (isEdit) handleUpdate(basicData, shopData);
    else handleCreate(basicData, shopData);
  };

  // ------------------------
  // CREATE SUBSTORE
  // ------------------------
  const handleCreate = async (basicData, shopData) => {
    const formattedPhone = basicData?.phone?.startsWith("0")
      ? basicData?.phone.substring(1)
      : basicData?.phone;

    const payload = {
      master_store_id,
      email: basicData?.email.toLowerCase(),
      phone: formattedPhone,
      password: basicData?.password,
      warehouse_name: basicData?.warehouse_name,
      longitude: shopData?.longitude,
      latitude: shopData?.latitude,
      locality: shopData?.locality,
      area: shopData?.area,
      state: shopData?.state,
      street: shopData?.street,
      street2: shopData?.street2,
      zip: shopData?.zip,
      city: shopData?.city,
      zone_id: shopData?.zone_id || null,
    };

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        URLS.CREATE_WAREHOUSE_STORE,
        payload
      );

      if (res?.data?.result?.status_code === 200) {
        toast.success(t("Warehouse Created Successfully"));
        navigate("/warehouse/list");
        onClose();
      } else {
        toast.error(res?.data?.result?.status || "Something went wrong");
      }
    } catch {
      toast.error("Failed to create sub-store.");
    } finally {
      setLoading(false);
      setShowOTPModal(false);
    }
  };

  // ------------------------
  // UPDATE SUBSTORE
  // ------------------------
  const handleUpdate = async (basicData, shopData) => {
    const payload = {
      warehouse_id: editData?.warehouse_id,
      phone: basicData?.phone,
      password: basicData?.password,
      warehouse_name: basicData?.warehouse_name,
      longitude: shopData?.longitude,
      latitude: shopData?.latitude,
      locality: shopData?.locality,
      area: shopData?.area,
      state: shopData?.state,
      street: shopData?.street,
      street2: shopData?.street2,
      zip: shopData?.zip,
      city: shopData?.city,
      zone_id: shopData?.zone_id || null,
    };

    if (basicData?.email !== oldEmail)
      payload.email = basicData?.email.toLowerCase();

    try {
      setLoading(true);
      const res = await axiosInstance.post(URLS.UPDATE_WAREHOUSE_DATA, payload);

      if (res?.data?.result?.status_code === 200) {
        toast.success(t("Warehouse Updated Successfully"));
        setIsDirty(false);
        onClose();
        // Do NOT clear data after update
      } else {
        toast.error(res?.data?.result?.status || "Something went wrong");
      }
    } catch {
      toast.error("Failed to update sub-store.");
    } finally {
      setLoading(false);
      setShowOTPModal(false);
    }
  };

  // ------------------------
  // SEND OTP
  // ------------------------
  const sendOtp = async (phone) => {
    const formattedPhone = phone?.startsWith("0") ? phone.substring(1) : phone;
    const payload = { new_mobile: formattedPhone, country_code: "+20" };

    try {
      dispatch(signupActions.signupRequest());
      const response = await dispatch(signupUser(payload));

      if (
        response?.payload?.result?.status ===
          "Retailer Already exist with this mobile" ||
        response?.payload?.result?.status === "Number already exist"
      ) {
        toast.error(t("Phone Number already exist"));
        return;
      }

      setShowOTPModal(true);
    } catch {
      toast.error("OTP sending failed");
    }
  };

  // ------------------------
  // BUTTON STATES
  // ------------------------
  const isSaveDisabled = !isFormValid || (isEdit && !isDirty);
  const isCancelDisabled = isEdit ? !isDirty : false;

  return (
    <>
      <div className="flex justify-end me-8 gap-4 mt-4 mb-2">
        <button
          onClick={handleCancel}
          disabled={isCancelDisabled}
          className={`w-[140px] h-[45px] rounded-lg font-semibold border transition ${
            isCancelDisabled
              ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
              : "bg-white text-primaryBlue border-primaryBlue"
          }`}
        >
          {t("Cancel")}
        </button>

        <button
          onClick={handleSave}
          disabled={isSaveDisabled}
          className={`w-[140px] h-[45px] rounded-lg font-semibold text-white transition ${
            isSaveDisabled
              ? "bg-primaryBluedisabled opacity-50 cursor-not-allowed"
              : "bg-primaryBlue"
          }`}
        >
          {isEdit ? t("Update") : t("Save")}
        </button>
      </div>
      <div className="flex bg-white rounded-xl ms-8 me-8 min-h-screen scrollbar-none relative">
        {loading && <LoadingSpinner />}
        <div className="w-full min-h-screen overflow-y-auto p-6">
          <div className=" mx-auto space-y-8">
            <SubStoreLocation
              ref={shopLocationRef}
              defaultData={shopLocationData}
              isEdit={isEdit}
              onChange={(data) => setShopLocationData(data)}
            />

            <SubStoreBasicDeatils
              ref={basicDetailsRef}
              isWarehouse={true}
              defaultData={editData}
              isEdit={isEdit}
              onChange={(data) => {
                console.log("====change====");

                setBasicDetailsData(data);
              }}
            />

            {/* <div className="flex justify-end gap-4 mt-10">
                        <button
                            onClick={handleCancel}
                            disabled={isCancelDisabled}
                            className={`w-[140px] h-[45px] rounded-lg font-semibold border transition ${isCancelDisabled
                                ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                                : "bg-white text-primaryBlue border-primaryBlue"
                                }`}
                        >
                            {t("Cancel")}
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={isSaveDisabled}
                            className={`w-[140px] h-[45px] rounded-lg font-semibold text-white transition ${isSaveDisabled
                                ? "bg-primaryBluedisabled opacity-50 cursor-not-allowed"
                                : "bg-primaryBlue"
                                }`}
                        >
                            {isEdit ? t("Update") : t("Save")}
                        </button>
                    </div> */}
          </div>
        </div>

        <OTPModal
          type="substore"
          responseData={phoneNumber}
          mobile={basicDataSend?.phone}
          isOpen={showOTPModal}
          numberChange={() => setShowOTPModal(false)}
          onClose={() => setShowOTPModal(false)}
          payload={() => setShowOTPModal(false)}
          createSubstore={handleOtpVerified}
        />
      </div>
    </>
  );
};

export default CreateWarehouse;
